import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ShoppingCart, PlayCircle, StopCircle, Clock, Target } from 'lucide-react';
import { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const fmt = (n, d = 2) => (typeof n === 'number' && isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d }) : '—');

/**
 * MM audit 3.1: TWAP buyback executor.
 *
 * Админ задаёт target_usd + window_minutes + size range → сервис
 * разбивает на random chunks с Poisson-задержками между ними. На UI
 * видно прогресс в реальном времени (каждые 10 сек).
 *
 * Все данные — с реального API, никаких моков.
 */
const BuybackCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // form
  const [target, setTarget] = useState('500');
  const [windowMin, setWindowMin] = useState('240');
  const [minChunk, setMinChunk] = useState('5');
  const [maxChunk, setMaxChunk] = useState('15');
  const [slippage, setSlippage] = useState('0.5');

  const loadStatus = async () => {
    try {
      const r = await fetch(`${API_URL}/api/market-maker/buyback/status`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus(await r.json());
    } catch (e) {
      console.warn('buyback status load failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 10000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      const payload = {
        target_usd: parseFloat(target),
        window_minutes: parseInt(windowMin, 10),
        min_chunk_usd: parseFloat(minChunk),
        max_chunk_usd: parseFloat(maxChunk),
        slippage_pct: Math.max(0.05, Math.min(1.0, parseFloat(slippage) || 0.5)),
      };
      const r = await fetch(`${API_URL}/api/market-maker/buyback/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(`TWAP buyback started: $${data.session.target_usd}, ${data.session.chunks_planned} chunks`);
      await loadStatus();
    } catch (e) {
      toast.error(`Failed to start buyback: ${e.message || e}`);
    } finally {
      setStarting(false);
    }
  };

  const cancel = async () => {
    if (!status?.active_session) return;
    const sid = status.active_session.id;
    setCancelling(true);
    try {
      const r = await fetch(`${API_URL}/api/market-maker/buyback/cancel/${sid}?reason=admin_cancel`, {
        method: 'POST',
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success(`Buyback ${sid} cancelled`);
      await loadStatus();
    } catch (e) {
      toast.error(`Cancel failed: ${e.message || e}`);
    } finally {
      setCancelling(false);
    }
  };

  const session = status?.active_session || null;
  const summary = status?.chunks_summary || {};

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShoppingCart className="h-4 w-4" /> TWAP Buyback Executor
          {session && (
            <Badge variant="default" className="bg-emerald-600 animate-pulse text-xs">
              RUNNING
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          Admin buyback с рандомизированными chunks и Poisson-задержками для защиты от front-run.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-slate-400 text-sm">Loading...</div>
        ) : session ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <div className="text-xs text-slate-400"><Target className="h-3 w-3 inline mr-1"/>Target</div>
                <div className="text-lg font-bold">${fmt(session.target_usd)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Spent</div>
                <div className="text-lg font-bold">${fmt(session.spent_usd)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Tokens</div>
                <div className="text-lg font-bold">{fmt(session.tokens_received, 2)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Chunks</div>
                <div className="text-lg font-bold">
                  {session.chunks_done}/{session.chunks_planned}
                  {session.chunks_failed > 0 && (
                    <span className="text-red-400 text-xs ml-2">{session.chunks_failed} failed</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400"><Clock className="h-3 w-3 inline mr-1"/>Window</div>
                <div className="text-lg font-bold">{session.window_minutes}m</div>
              </div>
            </div>

            <div className="h-2 bg-slate-800 rounded overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{
                  width: `${Math.min(100, (session.chunks_done / Math.max(1, session.chunks_planned)) * 100)}%`,
                }}
              />
            </div>

            <div className="text-xs text-slate-400 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>Ref price: ${fmt(session.reference_price_usd, 8)}</div>
              <div>Avg filled: ${fmt(session.avg_filled_price_usd, 8)}</div>
              <div>Leakage: {session.leakage_bps == null ? '—' : `${fmt(session.leakage_bps, 1)} bps`}</div>
              <div>Slippage: {fmt(session.slippage_pct, 2)}%</div>
            </div>

            <Button onClick={cancel} disabled={cancelling} variant="destructive" size="sm">
              <StopCircle className="h-4 w-4 mr-1" />
              {cancelling ? 'Cancelling...' : 'Cancel buyback'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div>
                <label className="text-xs text-slate-400">Target USD</label>
                <Input value={target} onChange={(e) => setTarget(e.target.value)} type="number" min="1" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Window (min)</label>
                <Input value={windowMin} onChange={(e) => setWindowMin(e.target.value)} type="number" min="1" max="2880" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Min chunk $</label>
                <Input value={minChunk} onChange={(e) => setMinChunk(e.target.value)} type="number" min="1" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Max chunk $</label>
                <Input value={maxChunk} onChange={(e) => setMaxChunk(e.target.value)} type="number" min="1" />
              </div>
              <div>
                <label className="text-xs text-slate-400">Slippage %</label>
                <Input value={slippage} onChange={(e) => setSlippage(e.target.value)} type="number" min="0.05" max="1.0" step="0.05" />
              </div>
            </div>
            <Button onClick={start} disabled={starting} className="bg-emerald-600 hover:bg-emerald-700">
              <PlayCircle className="h-4 w-4 mr-1" />
              {starting ? 'Starting...' : 'Start TWAP buyback'}
            </Button>
            <p className="text-xs text-slate-500">
              Chunks = target / avg(min,max). Задержки ~exp(N/window). Kill-switch останавливает
              новые chunks, но не отменяет уже отправленные.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BuybackCard;
